<template>
  <div class="ai-material-audit">
    <QueryForm ref="queryFormRef" @query="onQueryAction">
      <el-button type="primary" @click="onQueryAction" :loading="tableLoading">查询</el-button>
    </QueryForm>

    <Viewport v-model="tableHeight">
      <el-table v-loading="tableLoading" class="common-el-table-styles" border stripe :data="tableData" :height="tableHeight">
        <el-table-column prop="id" label="任务ID" width="60"> </el-table-column>
        <el-table-column prop="coverAddr" label="封面图" width="120">
          <template slot-scope="{ row }">
            <div style="display: flex; justify-content: center">
              <el-image fit="contain" :preview-src-list="[row.coverAddr]" style="height: 42px" :src="row.coverAddr"></el-image>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="imageName" label="视频名称" show-overflow-tooltip> </el-table-column>
        <el-table-column prop="gameName" label="游戏" show-overflow-tooltip width="220"> </el-table-column>
        <el-table-column prop="uploadOpName" label="上传人员" width="90"> </el-table-column>
        <el-table-column prop="taskTypeText" label="视频来源" width="90"> </el-table-column>
        <el-table-column prop="imageTypeText" label="素材类型" width="70"> </el-table-column>
        <el-table-column prop="uploadTimeText" label="上传时间" width="130"> </el-table-column>
        <el-table-column prop="spendSecondText" label="处理耗时" width="90"> </el-table-column>
        <el-table-column label="解析状态" width="65">
          <template slot-scope="{ row }">{{ AI_ANALYSIS_LIST.find((i) => i.id === row.analysisStatus)?.name || '-' }}</template>
        </el-table-column>
        <el-table-column label="评估状态" width="65">
          <template slot-scope="{ row }">{{ AI_ESTIMATE_LIST.find((i) => i.id === row.estimateStatus)?.name || '-' }}</template>
        </el-table-column>

        <el-table-column label="审核状态" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="getTagType(row.auditStatus)">{{ AI_AUDIT_LIST.find((i) => i.id === row.auditStatus)?.name || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="deadlineHourText" label="审核截止时间" width="90"> </el-table-column>
        <el-table-column prop="upOpName" label="审核人" width="90"> </el-table-column>
        <el-table-column label="操作" width="190">
          <template slot-scope="{ row }">
            <div>
              <el-button type="text" @click="onTableRowDetail(row)"> 详情 </el-button>

              <template v-if="accessAuth.includes('M21215')">
                <el-button type="text" v-if="row.auditStatus === AUDIT_STATES.AUDITING" @click="handleAudit(row)">去审核</el-button>

                <el-button type="text" @click="onTaskStatusInvoke(row, 2)" v-if="row.analysisStatus > ANALYSIS_STATES.ANALYZING">
                  重新解析
                </el-button>
                <el-button type="text" @click="onTaskStatusInvoke(row, 3)" v-if="row.estimateStatus > ESTIMATE_STATES.ESTIMATING">
                  重新评估
                </el-button>
                <el-button type="text" @click="onTaskStatusInvoke(row, 1)" v-if="row.auditStatus === AUDIT_STATES.FAILED">
                  重新审核
                </el-button>

                <el-button type="text" @click="onTaskStatusInvoke(row, 2)" v-if="row.analysisStatus < 1"> 素材解析 </el-button>
                <el-button type="text" @click="onTaskStatusInvoke(row, 3)" v-if="row.estimateStatus < 1"> 智能评估 </el-button>
                <el-button type="text" v-if="[AUDIT_STATES.AUDIT_REJECTED].includes(row.auditStatus)" @click="onTaskRemove(row)">
                  删除
                </el-button>
              </template>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          :current-page.sync="pager.pageIndex"
          :page-size.sync="pager.pageSize"
          :total="pager.total"
          :page-sizes="[30, 50, 100, 200]"
          @current-change="getTableData"
          @size-change="onQueryAction"
          layout="total, sizes, prev, pager, next, jumper"
          background
        >
        </el-pagination>
      </div>
    </Viewport>

    <el-dialog
      title="素材智能分析详情"
      :visible.sync="auditModalVisible"
      :close-on-click-modal="false"
      :before-close="onAuditModalBeforeClose"
      @close="onAuditModalClose"
      width="75%"
    >
      <AuditForm ref="auditFormRef" v-if="auditModalVisible" :modified.sync="auditModalModified" @subtitle-updated="getTableData">
        <template #btns>
          <el-button icon="el-icon-refresh" @click="onRedoTask">重新解析</el-button>
          <el-button icon="el-icon-check" type="primary" @click="onAuditPass">审核通过</el-button>
          <el-button style="width: 70px" icon="el-icon-close" type="danger" @click="onAuditReject">拒绝</el-button>
        </template>
      </AuditForm>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from 'vue'
import moment from 'moment'
import store from '@/store'
import { secondsToTimestamp } from '@/common/js/utils.js'
import { MessageBox } from 'element-ui'
import { Message } from 'view-design'
import QueryForm from './components/ai-audit/query-form.vue'
import AuditForm from './components/ai-audit/audit-form.vue'
import {
  getUploadedMaterialAuditList,
  redoAiAuditTask,
  aiAuditTaskPass,
  aiAuditTaskReject,
  aiAuditTaskUpdate,
  removeAiAuditTask,
  getAiAuditTaskDetail,
  aiAuditTaskTrigger
} from '@/api/material.model.js'
import {
  AI_MARTERIAL_AUDIT_STATES_ENUM as AUDIT_STATES,
  AI_MARTERIAL_ANALYSIS_STATES_ENUM as ANALYSIS_STATES,
  AI_MARTERIAL_ESTIMATE_STATES_ENUM as ESTIMATE_STATES,
  AI_ANALYSIS_LIST,
  AI_AUDIT_LIST,
  AI_ESTIMATE_LIST,
  TASK_TYPE_LIST,
  MATERIAL_TYPE_LIST
} from './consts'

const DASH = '-'
const tableHeight = ref(300)
const tableLoading = ref(false)
const auditModalVisible = ref(false)
const auditFormRef = ref(null)
const queryFormRef = ref(null)
const auditModalModified = ref(false)
const tableData = ref([])
const pager = ref({
  total: 0,
  pageIndex: 1,
  pageSize: 30
})

const accessAuth = computed(() => store.state.accessAuth)

const getTableData = () => {
  const params = queryFormRef.value.buildParams() || {}
  tableLoading.value = true
  getUploadedMaterialAuditList({
    ...params,
    pageIndex: pager.value.pageIndex,
    pageSize: pager.value.pageSize
  })
    .then((data) => {
      const { total, datas } = data
      pager.value.total = total

      if (!datas) {
        tableData.value = []
        return
      }

      tableData.value = datas.map((d) => {
        const spendSecondText = d.spendSecond && d.spendSecond > 1 ? secondsFormatter(d.spendSecond) : DASH
        const timeGapSeconds = (d.auditDeadlineTime - Date.now()) / 1000
        let deadlineHourText = DASH
        if (d.auditStatus === AUDIT_STATES.AUDITING) {
          deadlineHourText = timeGapSeconds > 0 ? `剩余${secondsFormatter(timeGapSeconds).slice(0, -3)}` : '剩余0分'
        }

        return {
          ...d,
          taskTypeText: TASK_TYPE_LIST.find((item) => item.id === d.taskType)?.name || DASH,
          imageTypeText: MATERIAL_TYPE_LIST.find((item) => item.id === d.type)?.name || DASH,
          uploadTimeText: moment(d.uploadTime).format('YYYY-MM-DD HH:mm:ss'),
          deadlineHourText,
          spendSecondText
        }
      })
    })
    .finally(() => {
      tableLoading.value = false
    })
}

const getTagType = (auditStatus) => {
  let tagType = ''
  switch (auditStatus) {
    case AUDIT_STATES.AUDITING:
      tagType = 'warning'
      break
    case AUDIT_STATES.AUDIT_REJECTED:
      tagType = 'danger'
      break
    case AUDIT_STATES.AUDIT_PASSED:
      tagType = 'success'
      break
    default:
      tagType = 'info'
      break
  }
  return tagType
}
const onQueryAction = () => {
  pager.value.pageIndex = 1
  getTableData()
}

onMounted(onQueryAction)

function secondsFormatter(sec) {
  return secondsToTimestamp(sec, true).replace(':', '时').replace(':', '分').replace('.', '秒').slice(0, -3)
}

function onAuditModalClose() {
  auditModalVisible.value = false
}

function onAuditModalBeforeClose(done) {
  if (auditModalModified.value) {
    MessageBox.confirm('您对当前内容进行了修改，离开前是否保存本次修改？', '提示', {
      confirmButtonText: '保存',
      cancelButtonText: '直接关闭',
      type: 'warning'
    })
      .then(() => {
        const submitParams = buildSubmitParams()
        aiAuditTaskUpdate(submitParams).then((data) => {
          if (data) {
            getTableData()
            done()
          }
        })
      })
      .catch(() => {
        done()
      })
      .finally(() => {
        auditModalModified.value = false
      })
  } else {
    done()
    auditModalModified.value = false
  }
}

function onAuditPass() {
  MessageBox.confirm('是否审核通过当前任务？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    const form = auditFormRef.value.getForm()
    const auditSuceess = await aiAuditTaskPass({ id: form.id })
    getTableData()
    onAuditModalClose()

    // 审核通过后进行json保存
    // 必须解析通过之后才能保存
    if (auditSuceess && form.analysisStatus === ANALYSIS_STATES.ANALYSIS_PASSED) {
      const submitParams = buildSubmitParams()
      await aiAuditTaskUpdate(submitParams)
    }
  })
}

function onAuditReject() {
  MessageBox.prompt('请输入拒绝理由', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    // 正则匹配非空字符，至少1个字符
    inputPattern: /\S{1,}/,
    inputErrorMessage: '拒绝理由不能为空'
  })
    .then(({ value }) => {
      const form = auditFormRef.value.getForm()
      aiAuditTaskReject({ id: form.id, reason: value }).then((data) => {
        if (data) {
          onAuditModalClose()
          getTableData()
        }
      })
    })
    .catch(() => {})
}

function buildSubmitParams() {
  const form = auditFormRef.value.getForm()
  const { keywords, suggested_tags, video_description } = form.analysisTaskResult
  const { overall_analysis, timeline_events } = video_description
  const jsonstr = JSON.stringify({
    suggested_tags: suggested_tags,
    keywords: keywords,
    video_description: {
      overall_analysis: overall_analysis,
      timeline_events: timeline_events.map((i) => {
        const { timeline_events_text, ...rest } = i
        return rest
      })
    }
  })

  return {
    id: form.id,
    taskResult: jsonstr
  }
}

function onRedoTask() {
  const form = auditFormRef.value.getForm()
  onTaskStatusInvoke({ id: form.id }, 2).then(() => {
    Message.success('已发起重新解析任务')
    onAuditModalClose()
    getTableData()
  })
}

/**
 * @description:
 * @param {*} row
 * @param {*} type integer 必须 1:审核;2:AI解析;3:AI评估
 * @return {*}
 */
function onTaskStatusInvoke(row, type) {
  return MessageBox.confirm(`是否执行${['', '', '解析', '评估'][type]}任务？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    aiAuditTaskTrigger({ id: row.id, type }).then((data) => {
      if (data) {
        Message.success(data || '已发起任务')
        getTableData()
      }
    })
  })
}

function onTableRowDetail(row) {
  auditModalVisible.value = true
  nextTick(() => {
    auditFormRef.value.setForm({ ...row, isEdit: true })
    auditFormRef.value.videoAutoPlay()
  })
}

function onTaskRemove(row) {
  MessageBox.confirm('是否删除当前任务？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    removeAiAuditTask({ id: row.id }).then((data) => {
      if (data) {
        Message.success('删除成功')
        getTableData()
      }
    })
  })
}

async function handleAudit(row) {
  try {
    const { auditDeadlineTime } = await getAiAuditTaskDetail({ id: row.id })
    const isValid = auditDeadlineTime && auditDeadlineTime - Date.now() > 0

    if (isValid) {
      auditModalVisible.value = true
      nextTick(() => {
        auditFormRef.value.setForm(row)
      })
    } else {
      Message.error('当前任务已过期，不能进行审核')
      getTableData()
    }
  } catch (e) {
    Message.error('获取任务详情错误')
  }
}

function handleAnalysis(row) {
  console.log('row output: ', row)
}

function handleEvaluation(row) {
  console.log('row output: ', row)
}
</script>

<style lang="scss" scoped>
.pager {
  text-align: right;
  padding: 10px 0;
}

::v-deep .audit-modal .el-dialog {
  max-width: 1200px;
  min-width: 1000px;
}
</style>
